import { useDocumentTitle } from "@/lib/use-document-title";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isRepeatedSignupResponse, translateAuthError } from "@/lib/auth-errors";
import { useAuth } from "@/lib/auth-context";
import { BackLink } from "@/components/BackLink";
import { customerUrl } from "@/lib/public-urls";

function SignupPage() {
  useDocumentTitle("Aktivera konto — Lagamobilen Access");
  const navigate = useNavigate();
  const { session, member, loading } = useAuth();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  // Persistera ref-kod så den överlever email-bekräftelseflödet
  useEffect(() => {
    if (refCode) {
      try { sessionStorage.setItem("lma_ref", refCode); } catch {}
    }
  }, [refCode]);

  if (!loading && session && member) {
    return <Navigate to="/access/dashboard" replace />;
  }

  async function logReferral(emailForRef: string) {
    const stored = refCode || (typeof window !== "undefined" ? sessionStorage.getItem("lma_ref") : null);
    if (!stored) return;
    const { data: referrer } = await supabase
      .from("members")
      .select("id")
      .eq("referral_code", stored)
      .maybeSingle();
    if (!referrer) return;
    await supabase.from("referrals").insert({
      referrer_member_id: referrer.id,
      referred_email: emailForRef,
      status: "pending",
      points_awarded: 0,
    } as any);
    try { sessionStorage.removeItem("lma_ref"); } catch {}
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    setInfo("");
    if (!email || !pwd) { setErr("Ange e-post och lösenord"); return; }
    if (pwd.length < 8) { setErr("Lösenordet måste vara minst 8 tecken"); return; }
    if (pwd !== pwd2) { setErr("Lösenorden matchar inte"); return; }
    if (!acceptTerms) { setErr("Du måste godkänna medlemsvillkor och integritetspolicy"); return; }

    setBusy(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password: pwd,
      options: { emailRedirectTo: customerUrl("/access/dashboard") },
    });
    setBusy(false);

    if (isRepeatedSignupResponse(data)) {
      setErr("Den här e-posten har redan ett konto. Logga in eller återställ lösenordet i stället.");
      return;
    }

    if (error) {
      setErr(translateAuthError(error.message));
      return;
    }

    await logReferral(email);

    if (data.session) {
      navigate("/access/dashboard");
    } else {
      navigate(`/access/bekrafta-mejl?email=${encodeURIComponent(email)}`);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-soft)]">
      <main className="flex items-center justify-center px-6 sm:px-10 py-12 lg:py-16">
        <div className="w-full max-w-md">
          <div className="mb-6"><BackLink fallback="/access/login" /></div>
          <h1 className="!text-3xl text-foreground">Aktivera ditt konto</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Använd samma e-postadress som du angav när du tecknade medlemskapet i butik.
          </p>

          {refCode && (
            <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-xs text-emerald-800">
              🎁 Du blev inbjuden med kod <span className="font-mono font-semibold">{refCode}</span> – välkomstgåva väntar när du aktiverar medlemskapet.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="block text-xs font-display font-semibold text-foreground mb-1.5">E-post</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border border-border focus:border-primary px-4 py-2.5 text-sm focus:outline-none transition"
                placeholder="din@email.se" autoComplete="email" />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-foreground mb-1.5">Lösenord</label>
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)}
                className="w-full rounded-lg border border-border focus:border-primary px-4 py-2.5 text-sm focus:outline-none transition"
                autoComplete="new-password" />
            </div>
            <div>
              <label className="block text-xs font-display font-semibold text-foreground mb-1.5">Upprepa lösenord</label>
              <input type="password" value={pwd2} onChange={e => setPwd2(e.target.value)}
                className="w-full rounded-lg border border-border focus:border-primary px-4 py-2.5 text-sm focus:outline-none transition"
                autoComplete="new-password" />
            </div>

            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={acceptTerms} onChange={e => setAcceptTerms(e.target.checked)} className="mt-0.5 accent-[var(--color-primary)]" />
              <span>
                Jag godkänner <Link to="/access/villkor" className="text-primary underline">medlemsvillkoren</Link> och har läst <Link to="/access/integritetspolicy" className="text-primary underline">integritetspolicyn</Link>.
              </span>
            </label>

            {err && <p className="text-destructive text-xs">{err}</p>}
            {info && <p className="text-[var(--color-success)] text-xs">{info}</p>}

            <button type="submit" disabled={busy}
              className="w-full inline-flex items-center justify-center py-3 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] transition text-sm font-display font-semibold tracking-wide disabled:opacity-60">
              {busy ? "Skapar konto…" : "Skapa konto"}
            </button>
          </form>

          <p className="text-sm text-muted-foreground text-center mt-8">
            Har du redan ett konto?{" "}
            <Link to="/access/login" className="text-primary hover:underline font-semibold">Logga in</Link>
          </p>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Inte medlem ännu? Medlemskap tecknas i någon av våra butiker i Malmö.
          </p>
        </div>
      </main>
    </div>
  );
}

export default SignupPage;
